import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { StatusBadge } from "@/components/app/ui-bits";
import { CaretDown, CaretRight, ListChecks, Warning, ClipboardText } from "@phosphor-icons/react";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

/**
 * Task Breakdown — grouped feed of tasks auto-synced from each deliverable.
 * Consumes GET /api/projects/{id}/tasks-grouped.
 *
 * Renders:
 * - Project totals (groups, tasks, unlinked)
 * - Per-deliverable collapsible group with:
 *   - Deliverable title + owner + due date + status
 *   - Tasks table (title · assignee · due date · status · priority)
 * - Unlinked tasks (no source deliverable) shown at the bottom
 */
export default function ProjectTaskBreakdown({ projectId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const inspector = useRecordInspector();

  useEffect(() => {
    if (!projectId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data: g } = await api.get(`/projects/${projectId}/tasks-grouped`);
        if (active) { setData(g); setErr(null); }
      } catch (e) {
        if (active) setErr(formatApiError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [projectId]);

  const toggle = (id) => setCollapsed((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  if (loading) {
    return (
      <section className="border-t border-slate-100 px-5 py-4" data-testid={`tasks-grouped-loading-${projectId}`}>
        <div className="overline mb-2 text-slate-500">Task Breakdown</div>
        <div className="font-mono-alt text-[11px] uppercase tracking-widest text-slate-400">Loading…</div>
      </section>
    );
  }

  if (err) {
    return (
      <section className="border-t border-slate-100 px-5 py-4" data-testid={`tasks-grouped-error-${projectId}`}>
        <div className="overline mb-2 text-slate-500">Task Breakdown</div>
        <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
          <Warning size={11} weight="fill" className="inline mr-1" /> {err}
        </div>
      </section>
    );
  }
  if (!data) return null;

  const { groups = [], unlinked = [], totals = {} } = data;

  return (
    <section className="border-t border-slate-100 px-5 py-4" data-testid={`tasks-grouped-${projectId}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="overline flex items-center gap-1 text-slate-500">
          <ListChecks size={12} /> Task Breakdown
        </div>
        <div className="flex flex-wrap items-center gap-3 font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">
          <span>{totals.groups || 0} groups</span>
          <span>{totals.tasks || 0} tasks</span>
          {totals.unlinked > 0 && <span className="text-amber-700">{totals.unlinked} unlinked</span>}
        </div>
      </div>

      {groups.length === 0 && unlinked.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500" data-testid={`tasks-empty-${projectId}`}>
          No tasks linked to this project&apos;s deliverables yet. Assign an owner to a deliverable — a task will auto-sync here.
        </div>
      ) : (
        <div className="space-y-2.5">
          {groups.map((g) => {
            const isCollapsed = collapsed.has(g.deliverable_id);
            return (
              <article key={g.deliverable_id} className="rounded-md border border-slate-200 bg-white"
                       data-testid={`tasks-group-${g.deliverable_id}`}>
                <header
                  className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-3 py-2 cursor-pointer hover:bg-slate-100/70"
                  onClick={() => toggle(g.deliverable_id)}
                >
                  {isCollapsed ? <CaretRight size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
                  <span className="font-medium text-sm text-slate-900">{g.deliverable_title || "Untitled deliverable"}</span>
                  {g.deliverable_status && <StatusBadge status={g.deliverable_status} />}
                  {g.owner && (
                    <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">
                      owner · {g.owner}
                    </span>
                  )}
                  {g.due_date && (
                    <span className="font-mono-alt text-[10px] text-slate-500">due {formatDate(g.due_date)}</span>
                  )}
                  <span className="ml-auto font-mono-alt text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">
                    {(g.tasks || []).length} task{(g.tasks || []).length === 1 ? "" : "s"}
                  </span>
                </header>
                {!isCollapsed && (
                  <div className="p-2">
                    {(g.tasks || []).length === 0 ? (
                      <div className="px-2 py-2 text-[11px] text-slate-500">No task generated yet for this deliverable.</div>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {(g.tasks || []).map((t) => (
                          <li key={t.id}
                              className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50"
                              onClick={() => inspector.open(t, {
                                title: t.title || "Task",
                                subtitle: `${g.deliverable_title || "Deliverable"} · ${t.assigned_to_name || t.assigned_to || "Unassigned"}`,
                              })}
                              data-testid={`task-row-${t.id}`}>
                            <span className="w-24 flex-shrink-0 font-mono-alt text-xs text-slate-500">
                              {formatDate(t.due_date) || "—"}
                            </span>
                            <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
                              {t.title || "Untitled task"}
                            </span>
                            <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">
                              {t.assigned_to_name || t.assigned_to || "—"}
                            </span>
                            {t.priority && t.priority !== "medium" && (
                              <span className="font-mono-alt text-[10px] uppercase rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">
                                {t.priority}
                              </span>
                            )}
                            <StatusBadge status={t.status} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </article>
            );
          })}

          {unlinked.length > 0 && (
            <article className="rounded-md border border-amber-200 bg-amber-50/40" data-testid={`tasks-unlinked-${projectId}`}>
              <header className="flex items-center gap-2 border-b border-amber-200 bg-amber-50/70 px-3 py-2">
                <ClipboardText size={12} className="text-amber-700" />
                <span className="font-medium text-sm text-amber-900">Unlinked tasks (no source deliverable)</span>
                <span className="ml-auto font-mono-alt text-[10px] rounded bg-white px-1.5 py-0.5 text-amber-800">
                  {unlinked.length}
                </span>
              </header>
              <ul className="divide-y divide-amber-100 p-2">
                {unlinked.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-amber-50"
                      onClick={() => inspector.open(t, { title: t.title || "Task", subtitle: `Unlinked · ${t.assigned_to_name || t.assigned_to || "Unassigned"}` })}
                      data-testid={`task-unlinked-row-${t.id}`}>
                    <span className="w-24 flex-shrink-0 font-mono-alt text-xs text-slate-500">{formatDate(t.due_date) || "—"}</span>
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{t.title || "Untitled task"}</span>
                    <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">
                      {t.assigned_to_name || t.assigned_to || "—"}
                    </span>
                    <StatusBadge status={t.status} />
                  </li>
                ))}
              </ul>
            </article>
          )}
        </div>
      )}
      <RecordInspector {...inspector.props} />
    </section>
  );
}
